/**
 * Simple Authentication Guard
 * Allows unauthenticated access but checks onboarding for authenticated users
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

interface AuthGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean; // If true, redirects unauthenticated users to login
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, requireAuth = false }) => {
  const { authStatus, isLoading, login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner fullscreen text="Checking authentication..." />;
  }

  // If route requires authentication and user is not authenticated, redirect to login
  if (requireAuth && authStatus === 'unauthenticated') {
    console.log('🔐 User not authenticated, redirecting to login...');
    // Add a small delay to prevent rapid redirects during token refresh attempts
    setTimeout(() => {
      login();
    }, 100);
    return <LoadingSpinner fullscreen text="Redirecting to login..." />;
  }

  // If authenticated, check onboarding status
  if (authStatus === 'authenticated' && user) {
    // Skip onboarding check if already on onboarding page, settings page, or in edit mode
    const isOnOnboardingPage = location.pathname === '/onboarding';
    const isOnSettingsPage = location.pathname === '/settings';
    const isEditMode = new URLSearchParams(location.search).get('edit') === 'true';
    
    if (!isOnOnboardingPage && !isOnSettingsPage && !isEditMode && user.onboarding_completed === false) {
      console.log('📝 User onboarding not completed, redirecting to onboarding...');
      navigate('/onboarding');
      return <LoadingSpinner fullscreen text="Redirecting to onboarding..." />;
    }
    
    return <>{children}</>;
  }

  // Allow unauthenticated access if requireAuth is false
  if (!requireAuth) {
    return <>{children}</>;
  }

  // Fallback
  return <LoadingSpinner fullscreen text="Loading..." />;
};

export default AuthGuard;
